import { Box, CardContent, CardHeader, Skeleton, TextField, Typography } from '@mui/material';
import { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { formatDecimal } from '../../../shared/utils/formatters';
import { StyledCard } from './Card.styles';
import {
  GetProductPriceGroupDto,
  ProductPriceGroupEnum,
  ProductPriceModel,
  ProductSizeDto,
} from '@kotipizzagroup/kotipizza-products-api-client';
import { useTranslation } from 'react-i18next';

type Props = {
  productSizeId: ProductSizeDto['productSizeId'];
  title: string;
  calculatedPrice: number;
  price: number;
  priceModel: ProductPriceModel;
  priceGroups: GetProductPriceGroupDto[];
  handleChange: (price: number, productSizeId: number) => void;
  handleGroupChange: (price: number, sizeId: number, group: ProductPriceGroupEnum) => void;
  isLoading?: boolean;
};

export const ProductPriceCard = (props: Props): JSX.Element => {
  const { title, calculatedPrice, price, handleChange, productSizeId, priceGroups, handleGroupChange, isLoading } =
    props;
  const [priceValue, setPriceValue] = useState<string>(String(price));
  const isPriceEdited = useRef(false);

  // Follow recalculated prices until the user types their own minimum price.
  useEffect(() => {
    if (!isPriceEdited.current) setPriceValue(String(price));
  }, [price]);

  const initialGroupPrices: Record<ProductPriceGroupEnum, string> = {
    [ProductPriceGroupEnum.Default]: '0',
    [ProductPriceGroupEnum.Tier1]: '0',
    [ProductPriceGroupEnum.Tier2]: '0',
    [ProductPriceGroupEnum.Tier3]: '0',
  };

  const defaultPrices = priceGroups.reduce<Record<ProductPriceGroupEnum, string>>((prev, curr) => {
    prev[curr.productPriceGroup] = String(curr.price);
    return prev;
  }, initialGroupPrices);

  const [priceGroupValues, setPriceGroupValues] = useState<Record<ProductPriceGroupEnum, string>>(defaultPrices);
  const { t } = useTranslation();

  const handlePriceChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newPrice = event.target.value.replace(',', '.');
    isPriceEdited.current = true;
    setPriceValue(String(newPrice));
    const priceNum = parseFloat(newPrice);
    if (!isNaN(priceNum)) {
      handleChange(priceNum, productSizeId);
    }
  };

  const onPriceGroupValueChange = (price: string, group: ProductPriceGroupEnum) => {
    const newPrice = price.replace(',', '.');
    const priceNum = parseFloat(price);
    setPriceGroupValues((prev) => {
      return {
        ...prev,
        [group]: newPrice,
      };
    });
    if (!isNaN(priceNum)) {
      handleGroupChange(priceNum, productSizeId, group);
    }
  };

  return (
    <StyledCard>
      <CardHeader title={title} />
      <CardContent>
        <>
          <>
            <CalculatedPrice>
              {t('calculated price')}:{' '}
              {isLoading ? (
                <Skeleton variant="text" width="4em" sx={{ display: 'inline-block' }} />
              ) : (
                formatDecimal(calculatedPrice, ' €')
              )}
            </CalculatedPrice>
            <Typography variant="h1">
              {isLoading ? <Skeleton variant="text" width="50%" sx={{ mx: 'auto' }} /> : formatDecimal(price, ' €')}
            </Typography>
            <PriceInput
              value={priceValue}
              type="text"
              label={t('Minimum price')}
              onChange={handlePriceChange}
              disabled={isLoading}
            />
          </>
          <Box sx={{ my: 2 }}>
            <Typography variant="h3">{t('Price groups')}</Typography>
            <PriceInput
              value={priceGroupValues.Default}
              type="text"
              label={t('Normal price')}
              disabled={isLoading}
              onChange={(e) => onPriceGroupValueChange(e.target.value, ProductPriceGroupEnum.Default)}
            />
            <PriceInput
              value={priceGroupValues.Tier1}
              type="text"
              label={t('productDetails.pricingTier', { tier: 1 })}
              disabled={isLoading}
              onChange={(e) => onPriceGroupValueChange(e.target.value, ProductPriceGroupEnum.Tier1)}
            />
            <PriceInput
              value={priceGroupValues.Tier2}
              type="text"
              label={t('productDetails.pricingTier', { tier: 2 })}
              disabled={isLoading}
              onChange={(e) => onPriceGroupValueChange(e.target.value, ProductPriceGroupEnum.Tier2)}
            />
            <PriceInput
              value={priceGroupValues.Tier3}
              type="text"
              label={t('productDetails.pricingTier', { tier: 3 })}
              disabled={isLoading}
              onChange={(e) => onPriceGroupValueChange(e.target.value, ProductPriceGroupEnum.Tier3)}
            />
          </Box>
        </>
      </CardContent>
    </StyledCard>
  );
};

const CalculatedPrice = styled.small`
  display: block;
  margin-bottom: 0.5rem;
`;

const PriceInput = styled(TextField)`
  margin-top: 1rem;
`;
